import type { UserOrganization } from "../api/users";
import { SELECTED_ORG_IDS_KEY } from "./constants";
import type { OrgContextOrganization } from "./types";

export function toOrgContextOrganization(org: UserOrganization): OrgContextOrganization {
  return {
    id: org.id,
    name: org.name,
    logoUrl: org.logoUrl,
    city: org.city,
    state: org.state,
  };
}

export function readStoredOrgIds(availableIds: string[]): string[] | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SELECTED_ORG_IDS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    const ids = parsed.map(String).filter((id) => availableIds.includes(id));
    return ids.length > 0 ? ids : null;
  } catch {
    return null;
  }
}

export function reconcileSelectedOrgIds(
  stored: string[] | null,
  availableIds: string[],
): string[] {
  if (availableIds.length === 0) return [];
  const fromStorage = stored?.filter((id) => availableIds.includes(id)) ?? [];
  if (fromStorage.length > 0) return fromStorage;
  return [...availableIds];
}

export function formatOrgContextLabel(orgs: OrgContextOrganization[]): string {
  if (orgs.length === 0) return "";
  if (orgs.length === 1) return orgs[0]!.name;
  return `${orgs.length} organizations`;
}

export function orgContextHash(ids: string[]): string {
  return [...ids].sort().join(",");
}

/**
 * Merge the given org IDs into the persisted selection so the context picks
 * them up on the next render cycle (e.g. after refreshUser brings new orgs in).
 * Call this BEFORE refreshUser() to stage the new IDs.
 */
export function stageAdditionalOrgIds(newIds: string[]): void {
  if (typeof sessionStorage === "undefined" || newIds.length === 0) return;
  try {
    const raw = sessionStorage.getItem(SELECTED_ORG_IDS_KEY);
    const existing: string[] = raw ? (JSON.parse(raw) as string[]) : [];
    const merged = Array.from(new Set([...existing, ...newIds]));
    sessionStorage.setItem(SELECTED_ORG_IDS_KEY, JSON.stringify(merged));
  } catch {
    /* ignore */
  }
}

export function resolveEffectiveOrgIds(
  tableFilter: unknown,
  isSuperAdmin: boolean,
  contextOrgIds: string[],
): string[] | undefined {
  const filterIds = Array.isArray(tableFilter)
    ? tableFilter.map(String).filter(Boolean)
    : [];

  if (isSuperAdmin) {
    return filterIds.length > 0 ? filterIds : undefined;
  }

  if (contextOrgIds.length === 0) {
    return filterIds.length > 0 ? filterIds : undefined;
  }

  if (filterIds.length > 0) {
    return filterIds.filter((id) => contextOrgIds.includes(id));
  }

  return contextOrgIds;
}
